import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Wallet, Plus, Pencil } from 'lucide-react';
import { ExpenseDialog } from '@/components/expenses/ExpenseDialog';

// Alian (audio): un driller/pump_installer con permiso de Expenses debe poder
// cargar un gasto (con recibo) desde el trabajo donde esta, SIN necesitar
// permiso para editar el trabajo. El backend ya filtra la lista a "solo los
// gastos que yo carge" cuando el rol no tiene view_all_expenses.
export default function JobExpensesCard({ job }) {
  const { hasPermission, user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canView = hasPermission('expenses', 'view');
  const canCreate = hasPermission('expenses', 'create');
  const canEdit = hasPermission('expenses', 'edit');

  const fetchExpenses = useCallback(async () => {
    if (!canView || !job?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/expenses', { params: { job_id: job.id } });
      setExpenses(res.data || []);
    } catch { /* non-blocking */ }
    finally { setLoading(false); }
  }, [canView, job?.id]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  if (!canView) return null;

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setDialogOpen(true); };

  return (
    <Card className="bg-white border-gray-100 shadow-sm" data-testid="job-expenses-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold font-['Barlow'] flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-600" /> Expenses {expenses.length > 0 && <span className="text-gray-400 font-normal">({expenses.length})</span>}
          </CardTitle>
          {canCreate && (
            <Button data-testid="job-add-expense-btn" size="sm" variant="outline" onClick={openCreate} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Expense
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
        ) : expenses.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-4" data-testid="job-no-expenses">No expenses logged for this job yet.</p>
        ) : (
          <div className="space-y-1.5">
            {expenses.map(e => (
              <div key={e.id} data-testid={`job-expense-row-${e.id}`} className="flex items-center justify-between gap-2 py-2 px-2.5 rounded-lg bg-gray-50 border border-gray-100">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{e.beneficiary_name || e.description || 'Expense'}</p>
                  <p className="text-[11px] text-gray-400">
                    {(e.date || '').slice(0, 10)} · Logged by {e.created_by_name || (e.created_by === user?.id ? 'you' : '—')}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${e.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-testid={`job-expense-status-${e.id}`}>
                    {e.payment_status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">${(e.amount || 0).toFixed(2)}</span>
                  {canEdit && (
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(e)} data-testid={`job-expense-edit-${e.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-gray-400" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {/* TEMA D7 (video "Ajustes de informe para Pump Installation"):
                Alian — "al final ponéle el amount total acá al final, en
                una línea" — una sola línea de resumen, no otra tarjeta. */}
            <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-100" data-testid="job-expenses-total-line">
              <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold">Total Expenses</span>
              <span className="text-sm font-bold text-gray-900" data-testid="job-expenses-total-amount">
                ${expenses.reduce((sum, e) => sum + (e.amount || 0), 0).toFixed(2)}
              </span>
            </div>
          </div>
        )}
      </CardContent>

      <ExpenseDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={fetchExpenses}
        editing={editing}
        defaultProjectId={job?.project_id || ''}
        defaultJobId={job?.id || ''}
      />
    </Card>
  );
}
